/**
 * Share codes from the issuer's side: which of an issuer's codes are still a
 * door onto a work, which have closed, and which works a new one may be issued
 * for.
 *
 * The recipient's side — redeeming one — is not here; it happens wherever the
 * code is typed and the API answers. The fetching and the form live in
 * `useShareCodesController.ts`, the words in `shareCodes.constants.ts`.
 *
 * `now` is always a parameter. Liveness is a comparison against the clock,
 * and the picker does not check the reader's rung: `issue_share_code` refuses
 * a work outside the curatable set, and the screen gates the form on
 * `canIssue` before that refusal can be met.
 *
 * TWO ISSUERS, TWO PICKERS, one everything else. An agent's issuable works are
 * their mandates; an owner's are the works they own. Both arrive here already
 * mapped to `IssuableWork` by the two functions at the foot of this file, so
 * `toShareCodesView` never learns which of them it is drawing — which is what
 * keeps the owner's arm from being a second copy of this screen.
 */

import type { AgentMandate, ShareCode, WorkPickerRow } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatConsignedDate } from "../gallery/gallery.model.ts";
import { SHARE_CODES_COPY } from "./shareCodes.constants.ts";
import type { ShareCodesCopy } from "./shareCodes.constants.ts";

/** One code as the row draws it, with every label already decided. */
export interface ShareCodeRow {
  shareCodeId: string;
  workId: string;
  title: string;
  ondoCode: string;
  code: string;
  /** 'for A buyer in Basel', or "". */
  intendedFor: string;
  note: string | null;
  /** 'issued by Daniel Ellsworth for Marlow & Finch', or 'issued by …'. */
  issuedBy: string;
  /** 'expires 21 Sep 2026' / 'expired 21 Sep 2026' / 'ended 14 Sep 2026'. */
  when: string;
  live: boolean;
  redeemedBy: string[];
  href: string;
}

/** A work the reader may issue a code for, as the picker lists it. */
export interface IssuableWork {
  workId: string;
  label: string;
}

export type ShareCodesView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | {
      kind: "ready";
      live: ShareCodeRow[];
      ended: ShareCodeRow[];
      works: IssuableWork[];
      /** Null when there is something to issue for. */
      noWorks: string | null;
      /** Null when at least one code has ever been issued. */
      empty: { message: string; detail: string } | null;
    };

/**
 * Everything the screen renders, decided here.
 *
 * `now` is a parameter, the rule every date-dependent screen keeps: a code's
 * liveness is a comparison against the clock, and a test should be able to put
 * one either side of its expiry without waiting a fortnight.
 */
export function toShareCodesView(input: {
  codes: ShareCode[] | null;
  /**
   * The picker's rows, already mapped — mandates for an agent, owned works for
   * an owner. Null while the read is in flight, which reads the same as none.
   */
  works: IssuableWork[] | null;
  error: string | null;
  workBase: string;
  copy: ShareCodesCopy;
  now?: Date;
}): ShareCodesView {
  const { codes, works: supplied, error, workBase, copy } = input;
  const now = input.now ?? new Date();

  if (error) return { kind: "error", title: copy.errorTitle, message: error };
  if (!codes) return { kind: "loading", message: copy.loading };

  const rows = codes.map((each) => toRow(each, workBase, now, copy));
  const works = supplied ?? [];

  return {
    kind: "ready",
    live: rows.filter((row) => row.live),
    ended: rows.filter((row) => !row.live),
    works,
    noWorks: works.length === 0 ? copy.issue.noWorks : null,
    empty: rows.length === 0 ? { message: copy.empty, detail: copy.emptyDetail } : null,
  };
}

/**
 * Live means open AND not yet expired. An expired code is filed with the
 * ended ones, because from the recipient's side the two are the same door
 * closed — and the row's own date says which of the two it was.
 */
export function isLive(code: ShareCode, now: Date): boolean {
  return code.endedAt === null && new Date(code.expiresAt) > now;
}

function toRow(
  code: ShareCode,
  workBase: string,
  now: Date,
  copy: ShareCodesCopy = SHARE_CODES_COPY,
): ShareCodeRow {
  return {
    shareCodeId: code.shareCodeId,
    workId: code.workId,
    title: code.title,
    ondoCode: code.ondoCode,
    code: code.code,
    intendedFor: code.intendedFor ? `${copy.intendedFor} ${code.intendedFor}` : "",
    note: code.note,
    issuedBy: describeIssuer(code, copy),
    when: describeWhen(code, now, copy),
    live: isLive(code, now),
    redeemedBy: code.redeemedBy,
    href: `${workBase}/${code.workId}`,
  };
}

/**
 * 'issued by Daniel Ellsworth for Marlow & Finch Art Advisory'.
 *
 * `copy` defaults, here and in `describeWhen`, because the words these two
 * read are the shared vocabulary rather than the voice — an owner's ledger
 * names its issuer in exactly the same breath as an agent's, and the owner's
 * copy inherits both strings by spreading the agent's. The parameter is taken
 * anyway so the default is a convenience rather than a hard-coded voice.
 */
export function describeIssuer(code: ShareCode, copy: ShareCodesCopy = SHARE_CODES_COPY): string {
  const firm = code.issuedForName ? ` ${copy.forFirm} ${code.issuedForName}` : "";

  return `${copy.issuedBy} ${code.issuedByName}${firm}`;
}

/**
 * The tense follows the state. Open: 'expires'. Ended by the issuer: 'ended',
 * on the date they did. Ran out: 'expired', on the date it did — which is the
 * same date as 'expires' with the verb turned, and that is the point.
 */
export function describeWhen(
  code: ShareCode,
  now: Date,
  copy: ShareCodesCopy = SHARE_CODES_COPY,
): string {
  if (code.endedAt) return `${copy.ended} ${formatConsignedDate(code.endedAt)}`;

  const verb = new Date(code.expiresAt) > now ? copy.expires : copy.expired;

  return `${verb} ${formatConsignedDate(code.expiresAt)}`;
}

/**
 * The works the reader may issue a code for: what they represent, deduped by
 * work — one client may have appointed the same firm twice across seats.
 *
 * The RUNG IS NOT CHECKED HERE. `my_agent_mandates()` returns every mandate a
 * viewer can read, and `issue_share_code` refuses a work outside the
 * curatable set. A viewer at an advisory therefore sees the picker and is
 * refused on submit — and `ShareCodes` gates the whole form on `canIssue`
 * from the workspace's rung, so that refusal is met by a sentence before the
 * form rather than by an error after it.
 */
export function issuableWorks(mandates: AgentMandate[]): IssuableWork[] {
  const seen = new Set<string>();
  const works: IssuableWork[] = [];

  for (const mandate of mandates) {
    if (seen.has(mandate.workId)) continue;
    seen.add(mandate.workId);
    works.push({
      workId: mandate.workId,
      label: [mandate.title, mandate.attributedName].filter(Boolean).join(" — "),
    });
  }

  return works;
}

/**
 * The OWNER's picker: the works they own, as `ondo.search_owned_works()`
 * returns them.
 *
 * A second mapper rather than a widened first one, because the two suppliers
 * have nothing in common but a title. A mandate is a row about an appointment
 * and carries `workId`; a `WorkPickerRow` is a row about a work and carries
 * `id`. Adapting either into the other's shape to save one function is how a
 * screen comes to believe an owner holds a mandate on their own work.
 *
 * `search_owned_works` is scoped by `owned_work_ids()` alone — 20260806090000
 * — which is one half of exactly the union `issue_share_code` accepts, so
 * every row this offers is a row the definer will take.
 *
 * THE RUNG IS NOT CHECKED HERE either, and for an owner there is no rung to
 * check: ownership is not a seat. The sentence above `issuableWorks` still
 * holds for the agent's side of the same picker.
 */
export function ownedIssuableWorks(rows: WorkPickerRow[]): IssuableWork[] {
  return rows.map((row) => ({
    workId: row.id,
    label: [row.title, row.attributedName].filter(Boolean).join(" — "),
  }));
}

export function describeShareCodesError(
  cause: unknown,
  copy: ShareCodesCopy = SHARE_CODES_COPY,
): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 429) return copy.errors.throttled;
    if (cause.status === 403) return copy.errors.refused;
    if (cause.message) return cause.message;
  }

  return copy.errorTitle;
}
